import { MetadataField, MetadataFieldType, MetadataValue } from '@/api/generatedApi';
import { MetadataEdit } from '@/components/AssetSidebar/AssetMetadata/MetadataEdit';
import { Ellipsis } from '@/components/Ellipsis';
import { useListAllMetadataFields } from '@/hooks/api/metadata/useListAllMetadataFields';
import { Box, Popover } from '@mui/material';
import { GridRenderCellParams } from '@mui/x-data-grid-pro';
import { useMemo, useState } from 'react';

interface EditableMetadataCellProperties {
  params: GridRenderCellParams;
  fieldName: string;
}

export const EditableMetadataCell = ({ params, fieldName }: EditableMetadataCellProperties) => {
  const [isEditing, setIsEditing] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [anchorElement, setAnchorElement] = useState<HTMLElement | undefined>();
  const [optimisticValue, setOptimisticValue] = useState<MetadataValue | undefined>();

  const { metadataFields = [] } = useListAllMetadataFields();

  const metadata = params.row.metadata;
  const serverValue = metadata && typeof metadata === 'object' ? metadata[fieldName] : undefined;
  const hasPendingOptimisticUpdate = optimisticValue !== undefined && optimisticValue !== serverValue;
  const currentValue = hasPendingOptimisticUpdate ? optimisticValue : serverValue;
  const displayValue = currentValue === undefined ? '' : String(currentValue);
  const isFolder = params.row.contentType === 'folder';

  const fieldType = useMemo(() => {
    const field = metadataFields.find((metadataField: MetadataField) => metadataField.name === fieldName);
    return field?.type || MetadataFieldType.Text;
  }, [metadataFields, fieldName]);

  const handleCellClick = (event: React.MouseEvent<HTMLElement>) => {
    if (isFolder) return;

    setAnchorElement(event.currentTarget);
    setIsEditing(true);
  };

  const handleEditSuccess = (savedValue?: MetadataValue) => {
    setOptimisticValue(savedValue);
    setIsEditing(false);
    setAnchorElement(undefined);
  };

  const handleEditCancel = () => {
    setIsEditing(false);
    setAnchorElement(undefined);
  };

  if (isFolder) {
    return (
      <Box
        data-testid='editable-metadata-cell-readonly'
        sx={{
          width: '100%',
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          padding: '8px',
          cursor: 'default',
        }}
      >
        {displayValue ? <Ellipsis text={displayValue} /> : ''}
      </Box>
    );
  }

  return (
    <>
      <Box
        data-testid='editable-metadata-cell'
        onClick={handleCellClick}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        sx={{
          width: '100%',
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          padding: '8px',
          cursor: 'pointer',
          backgroundColor: isEditing ? 'rgba(25, 118, 210, 0.12)' : 'transparent',
          border: isEditing ? '1px solid rgba(25, 118, 210, 0.5)' : '1px solid transparent',
          borderRadius: isEditing ? '4px' : '0px',
          transition: 'all 0.2s ease-in-out',
          '&:hover': {
            backgroundColor: isEditing ? 'rgba(25, 118, 210, 0.12)' : 'action.hover',
          },
        }}
      >
        {displayValue ? (
          fieldType === MetadataFieldType.Url ? (
            <a
              href={displayValue}
              target='_blank'
              rel='noopener noreferrer'
              onClick={event => event.stopPropagation()}
              style={{ color: 'inherit', textDecoration: 'underline' }}
            >
              <Ellipsis text={displayValue} />
            </a>
          ) : (
            <Ellipsis text={displayValue} />
          )
        ) : (
          isHovered && <span style={{ color: 'inherit', opacity: 0.5, fontStyle: 'italic' }}>Click to add...</span>
        )}
      </Box>
      <Popover
        open={isEditing}
        anchorEl={anchorElement}
        onClose={handleEditCancel}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        onKeyDown={event => event.stopPropagation()}
        slotProps={{
          paper: {
            elevation: 8,
            sx: {
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: '8px',
              overflow: 'visible',
            },
          },
        }}
      >
        <MetadataEdit
          asset={params.row}
          fieldName={fieldName}
          fieldType={fieldType}
          currentValue={currentValue}
          onSuccess={handleEditSuccess}
          onCancel={handleEditCancel}
        />
      </Popover>
    </>
  );
};
